import React, { useState, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import PaymentPermissionsPanel from './PaymentPermissionsPanel';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { CreditCard, Landmark, RotateCcw, CheckCircle2, AlertCircle, Loader2, Send, XOctagon } from 'lucide-react';

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const PaymentsSection = ({ proposal, onRefunded }) => {
  const payments = proposal?.payments || [];
  const totalAmount = Number(proposal?.total_amount || 0);
  const netPaid = payments
    .filter(p => !p.refunded)
    .reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const totalRefunded = payments
    .filter(p => p.refunded)
    .reduce((sum, p) => sum + Number(p.refund_amount || p.amount || 0), 0);
  const balanceDue = Math.max(totalAmount - netPaid, 0);

  const [refundTarget, setRefundTarget] = useState(null); // payment object
  const [refundAmount, setRefundAmount] = useState('');
  const [refunding, setRefunding] = useState(false);

  // Reminder modal state
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderNote, setReminderNote] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);

  // Cancel + Refund-all modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Payment permissions (partial payments / invoicing) live in PaymentPermissionsPanel.
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  const isCancelled = proposal?.status === 'cancelled';
  const hasUnrefundedPayments = payments.some(p => !p.refunded);

  const sendReminder = async () => {
    setSendingReminder(true);
    try {
      const res = await axios.post(`/proposals/${proposal.id}/send-payment-reminder`, {
        custom_message: reminderNote || null,
      });
      toast.success(`Reminder emailed to ${res.data.sent_to}`);
      setShowReminderModal(false);
      setReminderNote('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  const refundAllAndCancel = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`/inspyre/refund-all/${proposal.id}`);
      const { refunded_count, refunds, order_cancelled } = res.data;
      const failed = (refunds || []).filter(r => !r.approved);
      if (failed.length === 0) {
        toast.success(`Cancelled · ${refunded_count} payment(s) refunded${order_cancelled ? ' · order cancelled' : ''}`);
      } else {
        toast.warning(`Cancelled with ${failed.length} refund failure(s) — check the gateway`);
      }
      setShowCancelModal(false);
      try {
        const fresh = await axios.get(`/proposals/${proposal.id}`);
        onRefunded?.(fresh.data);
      } catch {}
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  };

  const openRefund = (payment) => {
    setRefundTarget(payment);
    setRefundAmount(String(Number(payment.amount || 0).toFixed(2)));
  };

  const doRefund = async () => {
    if (!refundTarget) return;
    const amt = parseFloat(refundAmount);
    if (!Number.isFinite(amt) || amt <= 0 || amt > Number(refundTarget.amount)) {
      toast.error('Refund must be between $0.01 and the original payment amount');
      return;
    }
    setRefunding(true);
    try {
      const res = await axios.post('/inspyre/refund', {
        transactionid: refundTarget.transactionid,
        amount: amt,
        proposal_id: proposal.id,
      });
      if (res.data?.approved) {
        toast.success(`Refunded ${fmt(amt)}`);
        // Reload the proposal so payments[] reflects refund flag
        try {
          const fresh = await axios.get(`/proposals/${proposal.id}`);
          onRefunded?.(fresh.data);
        } catch {}
        setRefundTarget(null);
      } else {
        const msg = res.data?.gateway?.responsetext || 'Refund declined by gateway';
        toast.error(msg);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally {
      setRefunding(false);
    }
  };

  const refreshProposal = async () => {
    try {
      const fresh = await axios.get(`/proposals/${proposal.id}`);
      onRefunded?.(fresh.data);
    } catch {}
  };
  const PaymentOptionsControl = () => (
    <div style={{ marginBottom: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0 12px 4px' }} data-testid="payment-options-control">
      <PaymentPermissionsPanel proposal={proposal} isAdmin={isAdmin} onUpdated={refreshProposal} />
    </div>
  );

  if (payments.length === 0) {
    return (
      <div style={{ marginBottom: 32, background: '#fff', padding: 24, borderRadius: 12, border: '1px solid #e2e8f0' }} data-testid="payments-section-empty">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a202c' }}>Payments</h3>
          <div style={{ fontSize: 13, color: '#718096' }}>
            Balance: <strong style={{ color: '#dc2626' }}>{fmt(totalAmount)}</strong>
          </div>
        </div>
        {balanceDue > 0 && !isCancelled && (
          <div style={{ marginBottom: 12 }}>
            <Button variant="outline" size="sm" onClick={() => setShowReminderModal(true)}
              data-testid="send-reminder-btn-empty">
              <Send size={14} style={{ marginRight: 6 }} /> Send Payment Reminder
            </Button>
          </div>
        )}
        {!isCancelled && balanceDue > 0 && <PaymentOptionsControl />}
        <div style={{ padding: '24px', textAlign: 'center', color: '#a0aec0', fontSize: 14, background: '#f8fafc', borderRadius: 8 }}>
          {isCancelled ? 'This proposal has been cancelled.' : (
            <>No payments received yet. Customer can pay at <code style={{ fontSize: 12 }}>/pay/{proposal.id}</code></>
          )}
        </div>

        {/* Reminder modal also needs to render in empty state */}
        <Dialog open={showReminderModal} onOpenChange={(o) => !o && setShowReminderModal(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Payment Reminder</DialogTitle>
              <DialogDescription>
                Email {proposal.customer_email} with a payment link and the current balance due.
              </DialogDescription>
            </DialogHeader>
            <div style={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, fontSize: 13 }}>
                <div>Customer: <strong>{proposal.customer_name}</strong></div>
                <div style={{ color: '#718096', fontSize: 12 }}>{proposal.customer_email}</div>
                <div style={{ marginTop: 6 }}>Balance due: <strong style={{ color: '#dc2626' }}>{fmt(balanceDue)}</strong></div>
              </div>
              <div>
                <Label>Optional personal note</Label>
                <Textarea value={reminderNote} onChange={(e) => setReminderNote(e.target.value)}
                  placeholder="e.g. Hey Jose, just a friendly reminder — these polos are ready to ship as soon as we get the balance. Thanks!"
                  rows={3} data-testid="reminder-note-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReminderModal(false)} disabled={sendingReminder}>Cancel</Button>
              <Button onClick={sendReminder} disabled={sendingReminder || balanceDue <= 0}
                data-testid="confirm-reminder-btn">
                {sendingReminder ? <><Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} />Sending...</> : <><Send size={14} style={{ marginRight: 6 }} />Send Reminder</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 32, background: '#fff', padding: 24, borderRadius: 12, border: '1px solid #e2e8f0' }} data-testid="payments-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a202c' }}>Payments</h3>
        <div style={{ display: 'flex', gap: 16, fontSize: 13, flexWrap: 'wrap' }}>
          <span style={{ color: '#718096' }}>Total: <strong style={{ color: '#1a202c' }}>{fmt(totalAmount)}</strong></span>
          <span style={{ color: '#22c55e' }}>Paid: <strong>{fmt(netPaid)}</strong></span>
          {totalRefunded > 0 && (
            <span style={{ color: '#f59e0b' }}>Refunded: <strong>{fmt(totalRefunded)}</strong></span>
          )}
          <span style={{ color: balanceDue > 0 ? '#dc2626' : '#22c55e' }}>
            Balance: <strong>{fmt(balanceDue)}</strong>
          </span>
        </div>
      </div>

      {/* Action buttons */}
      {!isCancelled && balanceDue > 0 && <PaymentOptionsControl />}
      {!isCancelled && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {balanceDue > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowReminderModal(true)}
              data-testid="send-reminder-btn">
              <Send size={14} style={{ marginRight: 6 }} /> Send Payment Reminder
            </Button>
          )}
          {hasUnrefundedPayments && (
            <Button variant="outline" size="sm" onClick={() => setShowCancelModal(true)}
              style={{ borderColor: '#fecaca', color: '#dc2626' }}
              data-testid="refund-cancel-btn">
              <XOctagon size={14} style={{ marginRight: 6 }} /> Refund + Cancel Order
            </Button>
          )}
        </div>
      )}
      {isCancelled && (
        <div style={{ marginBottom: 16, background: '#fef2f2', border: '1px solid #fecaca', padding: 12, borderRadius: 8, color: '#991b1b', fontSize: 13, fontWeight: 600 }}>
          ⚠️ This proposal has been cancelled.
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f7fafc', textTransform: 'uppercase', fontSize: 11, color: '#718096' }}>
            <tr>
              <th style={{ padding: '10px 12px', textAlign: 'left' }}>Date</th>
              <th style={{ padding: '10px 12px', textAlign: 'left' }}>Method</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>Amount</th>
              <th style={{ padding: '10px 12px', textAlign: 'left' }}>Transaction ID</th>
              <th style={{ padding: '10px 12px', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}></th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p, i) => (
              <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }} data-testid={`payment-row-${i}`}>
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#4a5568' }}>
                  {formatDate(p.created_at)}
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {p.method === 'ach' ? <Landmark size={14} /> : <CreditCard size={14} />}
                    {p.method === 'ach' ? 'ACH' : 'Card'}
                  </span>
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>
                  {fmt(p.amount)}
                </td>
                <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12, color: '#4a5568' }}>
                  {p.transactionid || '—'}
                  {p.authcode && <div style={{ fontSize: 10, color: '#a0aec0' }}>auth: {p.authcode}</div>}
                </td>
                <td style={{ padding: '10px 12px' }}>
                  {p.refunded ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#f59e0b', fontWeight: 500 }}>
                      <RotateCcw size={12} /> Refunded {fmt(p.refund_amount || p.amount)}
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#22c55e', fontWeight: 500 }}>
                      <CheckCircle2 size={12} /> Approved
                    </span>
                  )}
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                  {!p.refunded && (
                    <Button size="sm" variant="outline" onClick={() => openRefund(p)}
                      data-testid={`refund-btn-${i}`}>
                      <RotateCcw size={14} /> Refund
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Refund modal */}
      <Dialog open={!!refundTarget} onOpenChange={(o) => !o && setRefundTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refund Payment</DialogTitle>
            <DialogDescription>
              This will issue a refund through Inspyre Payments and credit the customer's original payment method.
            </DialogDescription>
          </DialogHeader>
          {refundTarget && (
            <div style={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, fontSize: 13 }}>
                <div>Original payment: <strong>{fmt(refundTarget.amount)}</strong></div>
                <div style={{ color: '#718096', fontSize: 12 }}>Txn {refundTarget.transactionid}</div>
              </div>
              <div>
                <Label>Refund amount</Label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#a0aec0' }}>$</span>
                  <Input type="number" step="0.01" min="0.01" max={refundTarget.amount}
                    value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)}
                    style={{ paddingLeft: 24 }}
                    data-testid="refund-amount-input" />
                </div>
                <div style={{ fontSize: 12, color: '#718096', marginTop: 4 }}>
                  Partial refunds are supported. Maximum: {fmt(refundTarget.amount)}
                </div>
              </div>
              <div style={{ background: '#fff7ed', border: '1px solid #fdba74', padding: 10, borderRadius: 6, display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#9a3412' }}>
                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <div>This action cannot be undone. The customer will see the refund on their statement within 3-5 business days.</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundTarget(null)} disabled={refunding}>Cancel</Button>
            <Button onClick={doRefund} disabled={refunding} style={{ background: '#dc2626', color: '#fff' }}
              data-testid="confirm-refund-btn">
              {refunding ? <><Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} />Refunding...</> : <>Refund {fmt(parseFloat(refundAmount) || 0)}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Reminder modal (populated state) */}
      <Dialog open={showReminderModal} onOpenChange={(o) => !o && setShowReminderModal(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Payment Reminder</DialogTitle>
            <DialogDescription>
              Email {proposal.customer_email} with a payment link and the current balance due.
            </DialogDescription>
          </DialogHeader>
          <div style={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, fontSize: 13 }}>
              <div>Customer: <strong>{proposal.customer_name}</strong></div>
              <div style={{ color: '#718096', fontSize: 12 }}>{proposal.customer_email}</div>
              <div style={{ marginTop: 6 }}>Balance due: <strong style={{ color: '#dc2626' }}>{fmt(balanceDue)}</strong></div>
            </div>
            <div>
              <Label>Optional personal note</Label>
              <Textarea value={reminderNote} onChange={(e) => setReminderNote(e.target.value)}
                placeholder="e.g. Hey Jose, just a friendly reminder — these polos are ready to ship as soon as we get the balance. Thanks!"
                rows={3} data-testid="reminder-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReminderModal(false)} disabled={sendingReminder}>Cancel</Button>
            <Button onClick={sendReminder} disabled={sendingReminder || balanceDue <= 0}
              data-testid="confirm-reminder-btn">
              {sendingReminder ? <><Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} />Sending...</> : <><Send size={14} style={{ marginRight: 6 }} />Send Reminder</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund-all + Cancel modal */}
      <Dialog open={showCancelModal} onOpenChange={(o) => !o && setShowCancelModal(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ color: '#dc2626' }}>Refund &amp; Cancel Order</DialogTitle>
            <DialogDescription>
              This will refund every approved payment AND cancel the production order in one step.
            </DialogDescription>
          </DialogHeader>
          <div style={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: 12, borderRadius: 8, fontSize: 13 }}>
              <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: 6 }}>What will happen:</div>
              <ul style={{ paddingLeft: 18, color: '#7f1d1d', margin: 0 }}>
                <li>{payments.filter(p => !p.refunded).length} payment(s) totaling <strong>{fmt(netPaid)}</strong> will be refunded via Inspyre</li>
                <li>The proposal status will be set to <strong>cancelled</strong></li>
                <li>The linked production order (if any) and its purchasing items will be cancelled</li>
              </ul>
            </div>
            <div style={{ background: '#fff7ed', border: '1px solid #fdba74', padding: 10, borderRadius: 6, display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#9a3412' }}>
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>This action cannot be undone. Refunds appear on the customer's statement in 3-5 business days.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelModal(false)} disabled={cancelling}>Keep Order</Button>
            <Button onClick={refundAllAndCancel} disabled={cancelling}
              style={{ background: '#dc2626', color: '#fff' }}
              data-testid="confirm-cancel-btn">
              {cancelling ? <><Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} />Processing...</> : <><XOctagon size={14} style={{ marginRight: 6 }} />Refund {fmt(netPaid)} &amp; Cancel</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentsSection;
